import BaseApi from '@core/api/model';

import { wait } from '@utils/async';

import User from '@/models/User';

export class VisibilityGroupApi extends BaseApi {
    static resource = 'visibility-groups';
    static mockItemCount = 0;
    static mockUseFilterOverrides = true;
    static mockFilterOverrideKeys = ['adviser_id'];

    get endpoints() {
        return {
            ...super.endpoints,
            updatePrimaryContacts: this.updatePrimaryContacts.bind(this),
            updateGrants: this.updateGrants.bind(this),
            updateChildren: this.updateChildren.bind(this),
            refreshAncestorStatisticsInBackground: this.refreshAncestorStatisticsInBackground.bind(this),
            refreshVisibilityGroupStatisticsByIdsInBackground:
                this.refreshVisibilityGroupStatisticsByIdsInBackground.bind(this),
            exportVisibilityGroups: this.exportVisibilityGroups.bind(this)
        };
    }

    get mock_endpoints() {
        return {
            ...super.mock_endpoints,
            updatePrimaryContacts: this.updatePrimaryContacts_mock.bind(this),
            updateGrants: this.updateGrants_mock.bind(this),
            updateChildren: this.updateChildren_mock.bind(this),
            refreshAncestorStatisticsInBackground: this.refreshAncestorStatisticsInBackground.bind(this),
            refreshVisibilityGroupStatisticsByIdsInBackground:
                this.refreshVisibilityGroupStatisticsByIdsInBackground.bind(this),
            exportVisibilityGroups: this.exportVisibilityGroups_mock.bind(this)
        };
    }

    get advisersBaseUrl() {
        return `/${this.constructor.version}/advisers`;
    }

    getAdviserVisibilityGroupsBaseUrl(adviserId) {
        return `${this.advisersBaseUrl}/${adviserId}/visibility-groups`;
    }

    normaliseIds(list = []) {
        if (!Array.isArray(list)) {
            return [];
        }

        return list
            .map(item => {
                if (!item) return null;
                if (typeof item === 'string') return item;
                if (typeof item === 'object' && item.id) return item.id;
                return null;
            })
            .filter(Boolean);
    }
    toVisibilityUser(item, primary_contact = false) {
        if (!item) {
            return null;
        }

        if (typeof item === 'object' && item.id) {
            // Keep only plain serialisable user fields. App-table rows can
            // include `$table_values` and other metadata with circular refs.
            return {
                id: item.id,
                name: item.name || null,
                email: item.email || null,
                primary_contact: Boolean(primary_contact)
            };
        }

        if (typeof item !== 'string') {
            return null;
        }

        const user = User.find(item);

        if (user) {
            return {
                id: user.id,
                name: user.name || `Mock User ${item}`,
                email: user.email || null,
                primary_contact
            };
        }

        const authUser = this._auth()?.user;

        if (authUser && `${authUser.id}` === `${item}`) {
            return {
                id: authUser.id,
                name: authUser.name || authUser.user_name || `Mock User ${item}`,
                email: authUser.email || authUser.user_email || null,
                primary_contact
            };
        }

        return {
            id: item,
            name: `Mock User ${item}`,
            email: null,
            primary_contact
        };
    }

    normaliseVisibilityGroupItem(item = {}, { recomputeCounts = false } = {}) {
        if (!item || typeof item !== 'object') {
            return item;
        }

        const hasParentField = Object.prototype.hasOwnProperty.call(item, 'parent');
        const parent_id = hasParentField
            ? item.parent && item.parent.id != null
                ? `${item.parent.id}`
                : null
            : item.parent_id;
        const contacts = Array.isArray(item.contacts) ? item.contacts : [];
        const grantees = Array.isArray(item.grantees) ? item.grantees : [];
        const children = Array.isArray(item.children) ? item.children : [];
        const investors = Array.isArray(item.investors) ? item.investors : [];

        return {
            ...item,
            parent_id,
            contacts,
            grantees,
            children,
            investors,
            contacts_count: recomputeCounts ? contacts.length : item.contacts_count ?? contacts.length,
            grantees_count: recomputeCounts ? grantees.length : item.grantees_count ?? grantees.length,
            children_count: recomputeCounts ? children.length : item.children_count ?? children.length,
            investors_count: item.investors_count ?? 0
        };
    }

    normaliseVisibilityGroupData(data, options = {}) {
        if (Array.isArray(data)) {
            return data.map(item => this.normaliseVisibilityGroupItem(item, options));
        }

        return this.normaliseVisibilityGroupItem(data, options);
    }

    toVisibilityGroupListItem(item = {}) {
        if (!item || typeof item !== 'object') {
            return item;
        }

        const normalised = this.normaliseVisibilityGroupItem(item);

        return {
            ...normalised,
            investors: undefined,
            contacts: undefined,
            grantees: undefined
        };
    }

    _filterGetResponseData(data) {
        return this.normaliseVisibilityGroupData(data);
    }

    _filterIndexResponseData(data) {
        if (Array.isArray(data)) {
            return data.map(item => this.toVisibilityGroupListItem(item));
        }

        return this.toVisibilityGroupListItem(data);
    }

    _filterSearchResponseData(data) {
        if (Array.isArray(data)) {
            return data.map(item => this.toVisibilityGroupListItem(item));
        }

        return this.toVisibilityGroupListItem(data);
    }

    _respondWithVisibilityGroupDetail(response) {
        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            response.data = this._filterGetResponseData(response.data);

            this._insertModel(response.data);
        });
    }

    /**
     * @param {string|null|undefined} adviserId
     * @param {string|null|undefined} id
     * @returns {object|null}
     */
    _getExistingVisibilityGroupFromOrm(adviserId, id) {
        const model = this.model;

        if (!model || typeof model.all !== 'function') {
            return null;
        }

        const idStr = id != null ? `${id}` : null;

        if (!idStr) {
            return null;
        }

        const scopedAdviserId = adviserId != null ? `${adviserId}` : null;

        return (
            model.all().find(group => {
                if (!group || group.id == null) {
                    return false;
                }

                if (`${group.id}` !== idStr) {
                    return false;
                }

                if (scopedAdviserId !== null && `${group.adviser_id}` !== scopedAdviserId) {
                    return false;
                }

                return true;
            }) || null
        );
    }

    _setAncestorStatisticsRefreshLoading(adviserId, parentIds = [], loading = false) {
        parentIds.forEach(id => {
            const parentGroup = this._getExistingVisibilityGroupFromOrm(adviserId, id);

            if (!parentGroup || parentGroup.id == null) {
                return;
            }

            this._insertModel({
                ...parentGroup,
                total_investors_count_loading: loading
            });
        });
    }

    _refreshVisibilityGroupStatisticsByIdsInBackground(adviserId, ids = [], useMock = false) {
        const parentIds = Array.from(new Set((Array.isArray(ids) ? ids : []).filter(id => id != null)));

        if (!parentIds.length) {
            return;
        }

        const filters = {
            adviser_id: `eq:${adviserId}`,
            id: `in:[${parentIds.join(',')}]`,
            include_statistics: true
        };

        try {
            this._setAncestorStatisticsRefreshLoading(adviserId, parentIds, true);
            const request = useMock
                ? this.index_mock(0, 100, null, filters)
                : this.index(null, null, null, filters, { persistent: true });
            Promise.resolve(request)
                .then(response => {
                    if (!response || response.status !== 'success' || !Array.isArray(response.data)) {
                        return;
                    }

                    // index/index_mock insert new rows only; explicitly upsert refreshed rows.
                    response.data.forEach(item => this._insertModel(item));
                })
                .finally(() => {
                    this._setAncestorStatisticsRefreshLoading(adviserId, parentIds, false);
                })
                .catch(() => {});
        } catch (error) {
            // silent by design
            this._setAncestorStatisticsRefreshLoading(adviserId, parentIds, false);
        }
    }

    refreshVisibilityGroupStatisticsByIdsInBackground(adviserId, ids = [], useMock = false) {
        return this._refreshVisibilityGroupStatisticsByIdsInBackground(adviserId, ids, useMock);
    }

    /**
     * Refresh ancestor rows from backend (silent fire-and-forget) so aggregate stats
     * like total_investors_count stay source-of-truth from API responses.
     * This only rehydrates returned rows; no broad list refresh.
     *
     * @param {string|null|undefined} adviserId
     * @param {string|null|undefined} targetGroupId Group that was just updated
     * @param {boolean} useMock When true, use index_mock
     */
    _refreshAncestorStatisticsInBackground(adviserId, targetGroupId, useMock = false) {
        const target = this._getExistingVisibilityGroupFromOrm(adviserId, targetGroupId);
        const parents = Array.isArray(target?.parents_to_root) ? target.parents_to_root : [];
        const parentIds = Array.from(new Set(parents.map(parent => parent?.id).filter(id => id != null)));

        this._refreshVisibilityGroupStatisticsByIdsInBackground(adviserId, parentIds, useMock);
    }

    refreshAncestorStatisticsInBackground(adviserId, targetGroupId, useMock = false) {
        return this._refreshAncestorStatisticsInBackground(adviserId, targetGroupId, useMock);
    }

    async post_mock(data = {}) {
        const name = typeof data?.name === 'string' ? data.name.trim() : '';

        if (!name.length) {
            throw new Error('name is required when creating a visibility group');
        }

        return await super.post_mock({
            ...data,
            name
        });
    }

    async index(offset = null, limit = null, sort_by = null, filters = {}, config = {}) {
        let extract = BaseApi.extractFilter(filters, 'adviser_id');

        if (!extract.value) {
            throw new Error(`adviser_id is required when requesting visibility groups.`);
        }

        const include_statistics = extract.filters.include_statistics ?? true;
        const filtersWithDefaults = { ...extract.filters, include_statistics };
        const params = {};

        if (offset) {
            params.offset = offset;
        }

        if (limit) {
            params.limit = limit;
        }

        if (sort_by) {
            params.sort_by = sort_by;
        }

        Object.assign(params, filtersWithDefaults);

        const requestConfig = {
            ...config,
            base_url: this.getAdviserVisibilityGroupsBaseUrl(extract.value)
        };

        const response = await BaseApi.api.get(requestConfig.base_url || this.base_url, params, requestConfig);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            response.data = this._filterIndexResponseData(response.data);

            this._insertModels(response.data);
        });
    }

    // eslint-disable-next-line no-unused-vars
    async index_mock(offset = 0, limit = 100, sort_by = null, filters = {}) {
        await wait(700);
        let adviser_id = null;

        if (typeof filters.adviser_id === 'string') {
            adviser_id = filters.adviser_id.replace(/^eq:/, '');
        } else if (filters.adviser_id) {
            adviser_id = filters.adviser_id;
        }

        adviser_id = adviser_id || 'ADVISER-ID';

        let name = null;

        if (typeof filters.name === 'string') {
            name = filters.name.replace(/^contains:/, '');
        }

        let items = this._getMocked();
        const hadPersistedItemsBeforeGenerate = items.length > 0;

        if (!items.length) {
            items = await this._generateMocked(this.constructor.mockItemCount, { adviser_id });
        }

        // Build full mock dataset first so relationships are persisted in
        // storage and remain consistent across subsequent PATCH requests.
        let fullData = items.map(item => {
            const cloned = { ...item };

            cloned.adviser_id = cloned.adviser_id || adviser_id;
            cloned.children = Array.isArray(cloned.children) ? cloned.children : [];

            return cloned;
        });

        // Only synthesise a default hierarchy when nothing in storage yet defines
        // parent/child links. Otherwise every index call would reset localStorage and
        // wipe relationships saved via updateChildren_mock.
        const noRelationshipsYet = fullData.every(item => !Array.isArray(item.children) || item.children.length === 0);

        if (fullData.length > 1 && (!hadPersistedItemsBeforeGenerate || noRelationshipsYet)) {
            // Reset children so we do not retain any previous structure.
            fullData = fullData.map(item => ({
                ...item,
                children: []
            }));

            const maxDepth = 5;
            // Create a single chain root -> item1 -> item2 -> ... up to maxDepth.
            const chainLength = Math.min(maxDepth, fullData.length);
            for (let i = 1; i < chainLength; i++) {
                const parentIndex = i - 1;
                const childIndex = i;

                fullData[`${parentIndex}`] = {
                    ...fullData[`${parentIndex}`],
                    children: [
                        ...fullData[`${parentIndex}`].children,
                        {
                            id: fullData[`${childIndex}`].id,
                            name: fullData[`${childIndex}`].name
                        }
                    ]
                };
            }

            // Any remaining items become direct children of the root.
            for (let i = chainLength; i < fullData.length; i++) {
                const root = fullData[0];
                fullData[0] = {
                    ...root,
                    children: [
                        ...root.children,
                        {
                            id: fullData[`${i}`].id,
                            name: fullData[`${i}`].name
                        }
                    ]
                };
            }
        }

        fullData = this.normaliseVisibilityGroupData(fullData, { recomputeCounts: true });
        this._saveMocked(fullData);

        let excludeIds = null;
        const idExtract = BaseApi.extractFilter(filters, 'id');

        if (idExtract.operator === 'not_in' && idExtract.value) {
            excludeIds = new Set(idExtract.value.replace(/[[\]]/g, '').split(',').filter(Boolean));
        }

        let data = fullData.filter(item => {
            if (name && !item.name.toLowerCase().includes(name.toLowerCase())) {
                return false;
            }

            if (excludeIds && excludeIds.has(item.id)) {
                return false;
            }

            return true;
        });

        const relationFilters = { ...filters };
        delete relationFilters.adviser_id;
        delete relationFilters.include_statistics;
        delete relationFilters.name;
        delete relationFilters.id;

        if (Object.keys(relationFilters).length > 0) {
            data = this._filterMocked(data, relationFilters);
        }

        const sliced = offset !== null && limit !== null ? data.slice(offset, offset + limit) : data;

        const response = {
            status: 'success',
            data: this._filterIndexResponseData(sliced),
            metadata: {
                total: data.length,
                limit,
                offset
            }
        };

        return this._respond(response, () => {
            this._insertModels(response.data);
        });
    }

    async search(criteria, filters = {}) {
        let extract = BaseApi.extractFilter(filters, 'adviser_id');

        if (!extract.value) {
            throw new Error(`adviser_id is required when searching for visibility groups.`);
        }

        const include_statistics = extract.filters.include_statistics ?? true;
        const filtersWithDefaults = { ...extract.filters, include_statistics };
        const response = await BaseApi.api.get(
            this.getAdviserVisibilityGroupsBaseUrl(extract.value),
            this._toSearchFilters(criteria, filtersWithDefaults)
        );

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            response.data = this._filterSearchResponseData(response.data);

            this._insertModels(response.data);
        });
    }

    async search_mock(criteria, filters = {}) {
        const mockFilters = { ...filters };
        delete mockFilters.include_statistics;

        let data = this._getMocked();

        if (!data.length) {
            return {
                status: 'success',
                data: []
            };
        }

        let nameCriteria = null;

        if (typeof criteria === 'string') {
            nameCriteria = criteria;
        } else if (criteria && typeof criteria === 'object') {
            if (typeof criteria.name === 'string') {
                nameCriteria = criteria.name;
            } else {
                const firstStringValue = Object.values(criteria).find(v => typeof v === 'string');
                nameCriteria = firstStringValue || null;
            }
        }

        if (typeof nameCriteria === 'string' && nameCriteria.trim().length) {
            data = data.filter(item => {
                if (!item.name || typeof item.name !== 'string') {
                    return false;
                }

                return item.name.toLowerCase().includes(nameCriteria.toLowerCase());
            });
        }

        data = this._filterMocked(data, mockFilters);

        const response = {
            status: 'success',
            data,
            metadata: {
                total: data.length,
                limit: data.length,
                offset: 0
            }
        };

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            response.data = this._filterSearchResponseData(response.data);

            this._insertModels(response.data);
        });
    }

    async exportVisibilityGroups(adviserId, format) {
        if (!adviserId) {
            throw new ReferenceError('adviserId is required when exporting visibility groups');
        }

        if (!['xlsx', 'pdf'].includes(format)) {
            throw new ReferenceError('format is required when exporting visibility groups');
        }

        const accept =
            format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

        return await BaseApi.api.post(
            `${this.advisersBaseUrl}/${adviserId}/visibility-groups/audit-report`,
            {},
            {
                responseType: 'blob',
                returnFullResponse: true,
                headers: {
                    Accept: accept
                }
            }
        );
    }

    async exportVisibilityGroups_mock(adviserId, format) {
        if (!adviserId) {
            throw new ReferenceError('adviserId is required when exporting visibility groups');
        }

        if (!['xlsx', 'pdf'].includes(format)) {
            throw new ReferenceError('format is required when exporting visibility groups');
        }

        await wait(300);

        const mimeType =
            format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const filename = `Visibility-Groups.${format}`;
        const content =
            format === 'pdf'
                ? '%PDF-1.4\n% Mock visibility groups export\n'
                : 'name,enabled,investor_count\nMock Group,true,12\n';

        return {
            data: new Blob([content], { type: mimeType }),
            request: {
                getResponseHeader: header => (String(header).toLowerCase() === 'x-filename' ? filename : null)
            }
        };
    }

    /**
     * Update the primary contacts for a visibility group.
     * @api {put} /visibility-groups/:adviserId/:id/primary-contacts Update visibility group primary contacts
     * @param {string} adviserId - The adviser ID.
     * @param {string} id - The visibility group ID.
     * @param {Array} items - The items to update. Accepts ids or full user objects; store as objects.
     */
    async updatePrimaryContacts(adviserId, id, items = [], config = {}) {
        if (!adviserId) {
            throw new ReferenceError('adviserId is required when updating visibility group primary contacts');
        }
        if (!id) {
            throw new ReferenceError('id is required when updating visibility group primary contacts');
        }

        const url = `${this.getAdviserVisibilityGroupsBaseUrl(adviserId)}/${id}/primary-contacts`;
        const ids = this.normaliseIds(items);

        const response = await BaseApi.api.put(url, { ids }, config);
        const result = this._respondWithVisibilityGroupDetail(response);
        this._refreshAncestorStatisticsInBackground(adviserId, id);

        return result;
    }

    /**
     * Mock the update primary contacts endpoint.
     * @api {put} /visibility-groups/:adviserId/:id/primary-contacts Update visibility group primary contacts
     * @param {string} adviserId - The adviser ID.
     * @param {string} id - The visibility group ID.
     * @param {Array} items - The items to update. Accepts ids or full user objects; store as objects.
     */
    async updatePrimaryContacts_mock(adviserId, id, items = []) {
        if (!adviserId) {
            throw new ReferenceError('adviserId is required when updating visibility group primary contacts');
        }
        if (!id) {
            throw new ReferenceError('id is required when updating visibility group primary contacts');
        }

        let data = this._getMocked();
        const index = data.findIndex(item => item.id === id);
        if (index === -1) {
            throw new Error(`Visibility group ${id} not found`);
        }

        const current = data[`${index}`];

        const primary_contacts = items.map(item => this.toVisibilityUser(item, true)).filter(Boolean);

        const updated = this.normaliseVisibilityGroupItem(
            {
                ...current,
                adviser_id: current.adviser_id || adviserId,
                contacts: primary_contacts
            },
            { recomputeCounts: true }
        );

        data[`${index}`] = updated;
        this._saveMocked(data);

        const response = {
            status: 'success',
            data: updated
        };

        return this._respond(response, () => {
            this._insertModel(updated);
            this._refreshAncestorStatisticsInBackground(adviserId, id, true);
        });
    }

    /**
     * Update the grants for a visibility group.
     * @api {put} /visibility-groups/:adviserId/:id/grants Update visibility group grants
     * @param {string} adviserId - The adviser ID.
     * @param {string} id - The visibility group ID.
     * @param {Array} items - The items to update. Accepts ids or full user objects; store as objects.
     */
    async updateGrants(adviserId, id, items = [], config = {}) {
        if (!adviserId) {
            throw new ReferenceError('adviserId is required when updating visibility group grants');
        }
        if (!id) {
            throw new ReferenceError('id is required when updating visibility group grants');
        }

        const url = `${this.getAdviserVisibilityGroupsBaseUrl(adviserId)}/${id}/grants`;
        const ids = this.normaliseIds(items);

        const response = await BaseApi.api.put(url, { ids }, config);
        const result = this._respondWithVisibilityGroupDetail(response);

        return result;
    }

    /**
     * Mock the update grants endpoint.
     * @api {put} /visibility-groups/:adviserId/:id/grants Update visibility group grants
     * @param {string} adviserId - The adviser ID.
     * @param {string} id - The visibility group ID.
     * @param {Array} items - The items to update. Accepts ids or full user objects; store as objects.
     */
    async updateGrants_mock(adviserId, id, items = []) {
        if (!adviserId) {
            throw new ReferenceError('adviserId is required when updating visibility group grants');
        }
        if (!id) {
            throw new ReferenceError('id is required when updating visibility group grants');
        }

        let data = this._getMocked();

        const index = data.findIndex(item => item.id === id);
        if (index === -1) {
            throw new Error(`Visibility group ${id} not found`);
        }

        const current = data[`${index}`];

        const grants = items.map(item => this.toVisibilityUser(item, false)).filter(Boolean);

        const updated = this.normaliseVisibilityGroupItem(
            {
                ...current,
                adviser_id: current.adviser_id || adviserId,
                grantees: grants
            },
            { recomputeCounts: true }
        );

        data[`${index}`] = updated;
        this._saveMocked(data);

        const response = {
            status: 'success',
            data: updated
        };

        return this._respond(response, () => {
            this._insertModel(updated);
        });
    }

    /**
     * Update the children for a visibility group.
     * @api {put} /visibility-groups/:adviserId/:id/children Update visibility group children
     * @param {string} adviserId - The adviser ID.
     * @param {string} id - The visibility group ID.
     * @param {Array} items - The items to update. Accepts ids or full user objects; store as objects.
     */
    async updateChildren(adviserId, id, items = [], config = {}) {
        if (!adviserId) {
            throw new ReferenceError('adviserId is required when updating visibility group children');
        }
        if (!id) {
            throw new ReferenceError('id is required when updating visibility group children');
        }

        const url = `${this.getAdviserVisibilityGroupsBaseUrl(adviserId)}/${id}/children`;
        const ids = this.normaliseIds(items);

        const response = await BaseApi.api.put(url, { ids }, config);
        const normalisedResponse = await this._respondWithVisibilityGroupDetail(response);
        this._refreshAncestorStatisticsInBackground(adviserId, id);

        this.syncReparentedChildrenInOrm(adviserId, id, ids);
        return normalisedResponse;
    }

    syncReparentedChildrenInOrm(adviserId, parentId, childIds = []) {
        const scopedAdviserId = adviserId != null ? `${adviserId}` : null;
        const targetParentId = parentId != null ? `${parentId}` : null;
        const movedChildIds = new Set(this.normaliseIds(childIds).map(id => `${id}`));

        if (!movedChildIds.size) {
            return;
        }

        const model = this.model;
        const allGroups = model && typeof model.all === 'function' ? model.all() : [];

        allGroups.forEach(group => {
            if (!group || group.id == null) {
                return;
            }

            if (scopedAdviserId !== null && `${group.adviser_id}` !== scopedAdviserId) {
                return;
            }

            if (targetParentId !== null && `${group.id}` === targetParentId) {
                return;
            }

            const children = Array.isArray(group.children) ? group.children : [];
            const filteredChildren = children.filter(child => child?.id != null && !movedChildIds.has(`${child.id}`));

            if (filteredChildren.length === children.length) {
                return;
            }

            this._insertModel(
                this.normaliseVisibilityGroupItem(
                    {
                        ...group,
                        children: filteredChildren
                    },
                    { recomputeCounts: true }
                )
            );
        });
    }

    /**
     * Mock the update children endpoint.
     * @api {put} /visibility-groups/:adviserId/:id/children Update visibility group children
     * @param {string} adviserId - The adviser ID.
     * @param {string} id - The visibility group ID.
     * @param {Array} items - The items to update. Accepts ids or full user objects; store as objects.
     */
    async updateChildren_mock(adviserId, id, items = []) {
        if (!adviserId) {
            throw new ReferenceError('adviserId is required when updating visibility group children');
        }
        if (!id) {
            throw new ReferenceError('id is required when updating visibility group children');
        }

        let data = this._getMocked();

        const parentIndex = data.findIndex(item => item.id === id);
        if (parentIndex === -1) {
            throw new Error(`Visibility group ${id} not found`);
        }

        const byId = data.reduce((acc, item) => {
            acc[item.id] = item;
            return acc;
        }, {});

        const ids = this.normaliseIds(items);

        // Ensure no child has multiple parents: remove these ids from all other items' children.
        data = data.map(item => {
            const cloned = { ...item };
            const children = Array.isArray(cloned.children) ? cloned.children : [];

            cloned.children = children.filter(child => !ids.includes(child.id));

            return cloned;
        });

        const parent = data[`${parentIndex}`];

        const childrenRefs = (items.length ? items : ids).map(child => {
            if (typeof child === 'string') {
                const found = byId[`${child}`];
                if (found) {
                    return { id: found.id, name: found.name };
                }
                return { id: child, name: child };
            }

            return {
                id: child.id,
                name: child.name || child.id
            };
        });

        const updatedParent = this.normaliseVisibilityGroupItem(
            {
                ...parent,
                adviser_id: parent.adviser_id || adviserId,
                children: childrenRefs
            },
            { recomputeCounts: true }
        );

        data[`${parentIndex}`] = updatedParent;
        this._saveMocked(data);

        const response = {
            status: 'success',
            data: updatedParent
        };

        return this._respond(response, () => {
            // Keep ORM in sync with all parent records affected by re-parenting.
            data.forEach(item => {
                this._insertModel(item);
            });
            this._refreshAncestorStatisticsInBackground(adviserId, id, true);
        });
    }
}

export default VisibilityGroupApi;
